import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fnApiCall } from '../config/networking';
import { useCallback, useMemo } from 'react';
import { useToast } from './useToast';

export const useInvalidateQuery = (path: string) => {
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [path] }),
    [queryClient, path]
  );

  return { invalidate };
};

const useApiMutation = <T = any, V = any>(
  method: string,
  url: string,
  id?: string | number,
  mutationOptions?: any
) => {
  const { invalidate } = useInvalidateQuery(url);
  const { addToast } = useToast();
  return useMutation<T, unknown, V>({
    mutationFn: (data: V) => {
      // data may be any shape; try to extract id when needed
      const anyData: any = data;
      const pathId = id ? id : anyData?.id;
      const path = pathId ? `${url}/${pathId}` : url;
      return fnApiCall(path, addToast, { method: method, data: anyData });
    },
    onSuccess: async () => {
      mutationOptions?.onSuccess?.();
      await invalidate();
    },
    ...mutationOptions
  });
};

export const useApiGet = <T = any>(url: string, options: any = {}) => {
  const { id, params } = options;
  const { addToast } = useToast();
  const urlParams = useMemo(
    () => (params ? `?${new URLSearchParams(params)}` : ''),
    [params]
  );
  const path = (id ? `${url}/${id}` : url) + urlParams;
  return useQuery<T>({
    queryKey: [url, params],
    queryFn: ({ signal }) => fnApiCall(path, addToast, { signal }),
    refetchOnWindowFocus: false,
    retry: false,
    ...options
  });
};

export const useApiPost = <T = any, V = any>(
  url: string,
  mutationOptions: any = null
) => {
  return useApiMutation<T, V>('post', url, undefined, mutationOptions);
};

export const useApiPut = <T = any, V = any>(
  url: string,
  id?: string | number,
  mutationOptions?: any
) => {
  return useApiMutation<T, V>('put', url, id, mutationOptions);
};

export const useApiDelete = <T = any>(
  url: string,
  id?: string,
  mutationOptions: any = null
) => {
  return useApiMutation<T>('delete', url, id, mutationOptions);
};
